import { FILTER_TYPES, DEFAULT_FILTER_VALUES } from '../constants/filterTypes';

/**
 * Utility class for managing filter operations
 */
export class FilterManager {
    /**
     * Create filter update batches for applying multiple filters at once
     * @param {Object} filters - Filter values object
     * @returns {Array} Array of filter update objects
     */
    static createFilterUpdates(filters) {
        return Object.keys(FILTER_TYPES).map(key => ({
            type: FILTER_TYPES[key],
            value: filters[`selected${key.charAt(0).toUpperCase() + key.slice(1).replace('_', '')}`] || 
                   filters[key] || 
                   DEFAULT_FILTER_VALUES[FILTER_TYPES[key]]
        }));
    }

    /**
     * Apply multiple filter updates using a generic handler
     * @param {Array} filterUpdates - Array of filter update objects
     * @param {Function} onFilterChange - Generic filter change handler
     */
    static applyFilterUpdates(filterUpdates, onFilterChange) {
        filterUpdates.forEach(({ type, value }) => {
            onFilterChange(type, value);
        });
    }

    /**
     * Get default filter state
     * @returns {Object} Default filter values
     */
    static getDefaultFilters() {
        return {
            searchText: DEFAULT_FILTER_VALUES[FILTER_TYPES.SEARCH],
            selectedStatus: DEFAULT_FILTER_VALUES[FILTER_TYPES.STATUS],
            selectedDateRange: DEFAULT_FILTER_VALUES[FILTER_TYPES.DATE_RANGE],
            selectedSellerAccount: DEFAULT_FILTER_VALUES[FILTER_TYPES.SELLER_ACCOUNT],
            selectedCustomizationStatus: DEFAULT_FILTER_VALUES[FILTER_TYPES.CUSTOMIZATION_STATUS],
            selectedPrinter: DEFAULT_FILTER_VALUES[FILTER_TYPES.PRINTER],
            selectedFulfillmentStatus: DEFAULT_FILTER_VALUES[FILTER_TYPES.FULFILLMENT_STATUS],
            selectedFulfillmentChannel: DEFAULT_FILTER_VALUES[FILTER_TYPES.FULFILLMENT_CHANNEL],
            selectedShipmentStatus: DEFAULT_FILTER_VALUES[FILTER_TYPES.SHIPMENT_STATUS],
            selectedSupporter: DEFAULT_FILTER_VALUES[FILTER_TYPES.SUPPORTER],
            selectedCategory: DEFAULT_FILTER_VALUES[FILTER_TYPES.CATEGORY],
            selectedMessageStatus: DEFAULT_FILTER_VALUES[FILTER_TYPES.MESSAGE_STATUS],
            selectedShipDateDue: DEFAULT_FILTER_VALUES[FILTER_TYPES.SHIP_DATE_DUE],
            selectedDeliveryDateDue: DEFAULT_FILTER_VALUES[FILTER_TYPES.DELIVERY_DATE_DUE],
            selectedConfirmShipment: DEFAULT_FILTER_VALUES[FILTER_TYPES.CONFIRM_SHIPMENT],
            selectedUpdatedLastMile: DEFAULT_FILTER_VALUES[FILTER_TYPES.UPDATED_LAST_MILE],
            selectedMockupSentAt: DEFAULT_FILTER_VALUES[FILTER_TYPES.MOCKUP_SENT_AT],
            selectedTitle: DEFAULT_FILTER_VALUES[FILTER_TYPES.TITLE],
            selectedSellerSku: DEFAULT_FILTER_VALUES[FILTER_TYPES.SELLER_SKU],
            selectedEmployeeId: DEFAULT_FILTER_VALUES[FILTER_TYPES.EMPLOYEE_ID],
            selectedExcludeTitle: DEFAULT_FILTER_VALUES[FILTER_TYPES.EXCLUDE_TITLE],
            selectedExcludeSellerSku: DEFAULT_FILTER_VALUES[FILTER_TYPES.EXCLUDE_SELLER_SKU]
        };
    }

    /**
     * Count active filters (non-default values)
     * @param {Object} filters - Current filter values
     * @returns {number} Number of active filters
     */
    static countActiveFilters(filters) {
        const {
            searchText,
            selectedStatus,
            selectedDateRange,
            selectedSellerAccount,
            selectedCustomizationStatus,
            selectedPrinter,
            selectedFulfillmentStatus,
            selectedFulfillmentChannel,
            selectedShipmentStatus,
            selectedSupporter,
            selectedCategory,
            selectedMessageStatus,
            selectedShipDateDue,
            selectedDeliveryDateDue,
            selectedConfirmShipment,
            selectedUpdatedLastMile,
            selectedMockupSentAt,
            selectedTitle,
            selectedSellerSku,
            selectedEmployeeId,
            selectedExcludeTitle,
            selectedExcludeSellerSku
        } = filters;

        console.log('FilterManager.countActiveFilters - selectedShipDateDue:', selectedShipDateDue); // Debug log

        let count = 0;
        if (searchText) count++;
        if (selectedStatus && Array.isArray(selectedStatus) && selectedStatus.length > 0) count++;
        if (selectedStatus && typeof selectedStatus === 'string' && selectedStatus !== 'all' && selectedStatus !== '') count++;
        if (selectedDateRange && selectedDateRange.length === 2) count++;
        if (selectedSellerAccount && Array.isArray(selectedSellerAccount) && selectedSellerAccount.length > 0) count++;
        if (selectedSellerAccount && typeof selectedSellerAccount === 'string' && selectedSellerAccount !== 'all' && selectedSellerAccount !== '') count++;
        if (selectedCustomizationStatus && selectedCustomizationStatus !== 'all') count++;
        if (selectedPrinter && Array.isArray(selectedPrinter) && selectedPrinter.length > 0) count++;
        if (selectedPrinter && typeof selectedPrinter === 'string' && selectedPrinter !== 'all' && selectedPrinter !== '') count++;
        if (selectedFulfillmentStatus && selectedFulfillmentStatus !== 'all') count++;
        if (selectedFulfillmentChannel && selectedFulfillmentChannel !== 'all') count++;
        if (selectedShipmentStatus && Array.isArray(selectedShipmentStatus) && selectedShipmentStatus.length > 0) count++;
        if (selectedShipmentStatus && typeof selectedShipmentStatus === 'string' && selectedShipmentStatus !== 'all' && selectedShipmentStatus !== '') count++;
        if (selectedSupporter && selectedSupporter !== 'all') count++;
        if (selectedCategory && Array.isArray(selectedCategory) && selectedCategory.length > 0) count++;
        if (selectedCategory && typeof selectedCategory === 'string' && selectedCategory !== 'all' && selectedCategory !== '') count++;
        if (selectedMessageStatus && selectedMessageStatus !== 'all') count++;
        if (selectedShipDateDue && selectedShipDateDue !== 'all') {
            console.log('Ship Date Due filter is active!'); // Debug log
            count++;
        }
        if (selectedDeliveryDateDue && selectedDeliveryDateDue !== 'all') count++;
        if (selectedConfirmShipment && selectedConfirmShipment !== 'all') count++;
        if (selectedUpdatedLastMile && selectedUpdatedLastMile !== 'all') count++;
        if (selectedMockupSentAt && selectedMockupSentAt !== 'all') count++;
        if (selectedTitle && selectedTitle.trim()) count++;
        if (selectedSellerSku && selectedSellerSku.trim()) count++;
        if (selectedEmployeeId && selectedEmployeeId.trim()) count++;
        if (selectedExcludeTitle && selectedExcludeTitle.trim()) count++;
        if (selectedExcludeSellerSku && selectedExcludeSellerSku.trim()) count++;
        
        console.log('Total active filters count:', count); // Debug log
        return count;
    }

    /**
     * Check if local filters have unapplied changes
     * @param {Object} localFilters - Local filter state
     * @param {Object} appliedFilters - Currently applied filters
     * @returns {boolean} True if there are unapplied changes
     */
    static hasUnappliedChanges(localFilters, appliedFilters) {
        const {
            searchText = '',
            selectedStatus = [],
            selectedDateRange = null,
            selectedSellerAccount = 'all',
            selectedCustomizationStatus = 'all',
            selectedPrinter = 'all',
            selectedFulfillmentStatus = 'all',
            selectedFulfillmentChannel = 'all',
            selectedShipmentStatus = [],
            selectedSupporter = 'all',
            selectedCategory = 'all',
            selectedMessageStatus = 'all',
            selectedShipDateDue = 'all',
            selectedDeliveryDateDue = 'all',
            selectedConfirmShipment = 'all',
            selectedUpdatedLastMile = 'all',
            selectedMockupSentAt = 'all',
            selectedTitle = '',
            selectedSellerSku = '',
            selectedEmployeeId = '',
            selectedExcludeTitle = '',
            selectedExcludeSellerSku = ''
        } = appliedFilters;

        // Special comparison for selectedStatus (array)
        const normalizeStatus = (status) => {
            if (Array.isArray(status)) return status;
            if (status === 'all' || status === '' || status === null || status === undefined) return [];
            return [status];
        };

        const normalizeCategory = (category) => {
            if (Array.isArray(category)) return category;
            if (category === 'all' || category === '' || category === null || category === undefined) return [];
            return [category];
        };

        const normalizePrinter = (printer) => {
            if (Array.isArray(printer)) return printer;
            if (printer === 'all' || printer === '' || printer === null || printer === undefined) return [];
            return [printer];
        };

        const normalizeSellerAccount = (sellerAccount) => {
            if (Array.isArray(sellerAccount)) return sellerAccount;
            if (sellerAccount === 'all' || sellerAccount === '' || sellerAccount === null || sellerAccount === undefined) return [];
            return [sellerAccount];
        };

        const normalizeShipmentStatus = (shipmentStatus) => {
            if (Array.isArray(shipmentStatus)) return shipmentStatus;
            if (shipmentStatus === 'all' || shipmentStatus === '' || shipmentStatus === null || shipmentStatus === undefined) return [];
            return [shipmentStatus];
        };

        const localStatusNormalized = normalizeStatus(localFilters.selectedStatus);
        const appliedStatusNormalized = normalizeStatus(selectedStatus);
        const localCategoryNormalized = normalizeCategory(localFilters.selectedCategory);
        const appliedCategoryNormalized = normalizeCategory(selectedCategory);
        const localPrinterNormalized = normalizePrinter(localFilters.selectedPrinter);
        const appliedPrinterNormalized = normalizePrinter(selectedPrinter);
        const localSellerAccountNormalized = normalizeSellerAccount(localFilters.selectedSellerAccount);
        const appliedSellerAccountNormalized = normalizeSellerAccount(selectedSellerAccount);
        const localShipmentStatusNormalized = normalizeShipmentStatus(localFilters.selectedShipmentStatus);
        const appliedShipmentStatusNormalized = normalizeShipmentStatus(selectedShipmentStatus);

        return (
            localFilters.searchText !== searchText ||
            JSON.stringify([...localStatusNormalized].sort()) !== JSON.stringify([...appliedStatusNormalized].sort()) ||
            JSON.stringify(localFilters.selectedDateRange) !== JSON.stringify(selectedDateRange) ||
            JSON.stringify([...localSellerAccountNormalized].sort()) !== JSON.stringify([...appliedSellerAccountNormalized].sort()) ||
            localFilters.selectedCustomizationStatus !== selectedCustomizationStatus ||
            JSON.stringify([...localPrinterNormalized].sort()) !== JSON.stringify([...appliedPrinterNormalized].sort()) ||
            localFilters.selectedFulfillmentStatus !== selectedFulfillmentStatus ||
            localFilters.selectedFulfillmentChannel !== selectedFulfillmentChannel ||
            JSON.stringify([...localShipmentStatusNormalized].sort()) !== JSON.stringify([...appliedShipmentStatusNormalized].sort()) ||
            localFilters.selectedSupporter !== selectedSupporter ||
            JSON.stringify([...localCategoryNormalized].sort()) !== JSON.stringify([...appliedCategoryNormalized].sort()) ||
            localFilters.selectedMessageStatus !== selectedMessageStatus ||
            localFilters.selectedShipDateDue !== selectedShipDateDue ||
            localFilters.selectedDeliveryDateDue !== selectedDeliveryDateDue ||
            localFilters.selectedConfirmShipment !== selectedConfirmShipment ||
            localFilters.selectedUpdatedLastMile !== selectedUpdatedLastMile ||
            localFilters.selectedMockupSentAt !== selectedMockupSentAt ||
            (localFilters.selectedTitle || '') !== selectedTitle ||
            (localFilters.selectedSellerSku || '') !== selectedSellerSku ||
            (localFilters.selectedEmployeeId || '') !== selectedEmployeeId ||
            (localFilters.selectedExcludeTitle || '') !== selectedExcludeTitle ||
            (localFilters.selectedExcludeSellerSku || '') !== selectedExcludeSellerSku
        );
    }
}
